/* eslint-disable lingui/no-unlocalized-strings */

import { useMemo } from "react";

import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";

import { CoachSectionSuggestions } from "./coach-section-suggestions";
import { resolveSuggestionSectionId } from "./coach-suggestion-sections";

/**
 * One-line context strip for a coach optimization run.
 *
 * All this keeps from the old CoachReviewPanel is the orientation: which job is being optimized for
 * and how far through the suggestions the student is. The suggestions themselves now live in the
 * section each one edits (see CoachSectionSuggestions), because a stack of rewrites at the top of
 * the sidebar gave no way to judge a change against the content around it.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const CoachReviewHeader = ({
  hiddenSectionIds = [],
}: {
  /**
   * Sections the sidebar is not rendering for this template (Masters' Union hides several).
   *
   * Passed in because only the sidebar knows what it mounted. A suggestion targeting a hidden
   * section would otherwise have no home at all, yet still count towards the total and towards the
   * projected score — so the student would be told to "accept all" with no way to reach one of
   * them. Those few get rendered here instead of being silently dropped.
   */
  hiddenSectionIds?: string[];
}) => {
  const session = useCoachOptimizationStore((state) => state.session);
  const suggestions = useCoachOptimizationStore((state) => state.suggestions);
  const skillAdditions = useCoachOptimizationStore((state) => state.skillAdditions);
  const acceptedIds = useCoachOptimizationStore((state) => state.acceptedIds);

  const orphanSectionIds = useMemo(() => {
    if (hiddenSectionIds.length === 0) return [];

    const hidden = new Set(hiddenSectionIds);
    const found = new Set<string>();

    for (const suggestion of suggestions) {
      const sectionId = resolveSuggestionSectionId(suggestion);
      if (hidden.has(sectionId)) found.add(sectionId);
    }

    return [...found];
  }, [suggestions, hiddenSectionIds]);

  if (!session) return null;

  const total = suggestions.length + skillAdditions.length;
  if (total === 0) return null;

  return (
    <section id="coach-review" className="grid gap-y-3">
      <header className="flex items-center justify-between gap-x-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-medium">AI Suggestions</h2>
          <p className="truncate text-xs text-foreground/55">
            {session.jobTitle}
            {session.companyName ? ` @ ${session.companyName}` : ""}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-success/10 px-2 py-1 text-[10px] text-success">
          {acceptedIds.length}/{total}
        </span>
      </header>

      {/* The "Suggestions appear inside the section they change" hint is gone. Sections now start
          collapsed with their "AI Suggested (n)" counts visible in the headers, so the structure
          explains itself and the sentence was describing what the student could already see. */}

      {orphanSectionIds.map((sectionId) => (
        <CoachSectionSuggestions key={sectionId} sectionId={sectionId} />
      ))}
    </section>
  );
};
